import { mortalWorks } from "@/mocks/portfolio";
import FadeIn from "@/components/base/FadeIn";
import TiltCard from "@/components/base/TiltCard";

export default function MmntoMori() {
  return (
    <section id="mmnto-mori" className="bg-foreground-950 text-background-50 py-24 md:py-36">
      <div className="px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/50 mb-4 title-eyebrow">
              ( 01 ) — Project Series
            </p>
            <h2 className="font-display font-black text-background-50 leading-[0.85] tracking-[-0.04em] text-[14vw] md:text-[10vw] lg:text-[9vw] whitespace-nowrap">
              <span className="title-caps">MMNTO</span>
              <em className="font-serif italic font-light title-italic">.Mori</em>
            </h2>
          </div>
          <div className="max-w-md">
            <p className="text-base md:text-lg text-background-50/70 leading-relaxed font-serif">
              A study in mortality — vanitas reimagined through twenty-first century eyes, where digital decay meets classical meditation on impermanence.
            </p>
          </div>
        </div>

        {/* Tag chips */}
        <div className="flex flex-wrap items-center gap-3 mb-12">
          {["Digital Art", "Vanitas", "Gen AI", "2035"].map((tag, i) => (
            <span
              key={i}
              className="px-4 py-2 rounded-full border border-background-50/30 text-background-50 text-xs uppercase tracking-[0.18em] font-display"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-6" data-product-shop>
          {/* Row 1 */}
          <FadeIn delay={0} className="col-span-12 md:col-span-5 group cursor-pointer">
            <TiltCard className="aspect-square bg-foreground-800 overflow-hidden mb-3" maxTilt={8} scale={1.02}>
              <img src={mortalWorks[0].image} alt={mortalWorks[0].id} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
              </div>
            </TiltCard>
            <span className="font-display text-xs uppercase tracking-[0.2em] text-background-50/80">{mortalWorks[0].id}</span>
          </FadeIn>
          <FadeIn delay={100} className="col-span-12 md:col-span-7 group cursor-pointer">
            <TiltCard className="aspect-[16/10] bg-foreground-800 overflow-hidden mb-3" maxTilt={8} scale={1.02}>
              <img src={mortalWorks[1].image} alt={mortalWorks[1].id} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
              </div>
            </TiltCard>
            <span className="font-display text-xs uppercase tracking-[0.2em] text-background-50/80">{mortalWorks[1].id}</span>
          </FadeIn>

          {/* Row 2 */}
          <FadeIn delay={200} className="col-span-12 md:col-span-4 group cursor-pointer">
            <TiltCard className="aspect-[3/4] bg-foreground-800 overflow-hidden mb-3" maxTilt={8} scale={1.02}>
              <img src={mortalWorks[2].image} alt={mortalWorks[2].id} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
              </div>
            </TiltCard>
            <span className="font-display text-xs uppercase tracking-[0.2em] text-background-50/80">{mortalWorks[2].id}</span>
          </FadeIn>
          <FadeIn delay={300} className="col-span-12 md:col-span-4 group cursor-pointer md:mt-16">
            <TiltCard className="aspect-[3/4] bg-foreground-800 overflow-hidden mb-3" maxTilt={8} scale={1.02}>
              <img src={mortalWorks[3].image} alt={mortalWorks[3].id} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
              </div>
            </TiltCard>
            <span className="font-display text-xs uppercase tracking-[0.2em] text-background-50/80">{mortalWorks[3].id}</span>
          </FadeIn>
          <FadeIn delay={400} className="col-span-12 md:col-span-4 group cursor-pointer">
            <TiltCard className="aspect-[3/4] bg-foreground-800 overflow-hidden mb-3" maxTilt={8} scale={1.02}>
              <img src={mortalWorks[4].image} alt={mortalWorks[4].id} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
              </div>
            </TiltCard>
            <span className="font-display text-xs uppercase tracking-[0.2em] text-background-50/80">{mortalWorks[4].id}</span>
          </FadeIn>

          {/* Row 3 - wide */}
          <FadeIn delay={500} className="col-span-12 group cursor-pointer">
            <TiltCard className="aspect-[16/7] bg-foreground-800 overflow-hidden mb-3" maxTilt={8} scale={1.02}>
              <img src={mortalWorks[5].image} alt={mortalWorks[5].id} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
              </div>
            </TiltCard>
            <span className="font-display text-xs uppercase tracking-[0.2em] text-background-50/80">{mortalWorks[5].id}</span>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}